import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { ClientOnly } from "~/components/ui/client-only";
import { formatCurrency } from "~/lib/format";
import type { YearSummary } from "~/types/financials";

interface RevenueChartProps {
	years: YearSummary[];
}

export function RevenueChart({ years }: RevenueChartProps) {
	const data = years.map((y) => ({
		year: y.year.toString(),
		revenue: y.revenue,
		netProfit: y.netProfit,
	}));

	// Hide the chart entirely when there is no revenue/profit data to plot
	// (e.g. micro entities that disclose only a balance sheet).
	const hasData = data.some((d) => d.revenue != null || d.netProfit != null);
	if (!hasData) return null;

	return (
		<div className="card-surface rounded-xl p-6">
			<h3 className="text-lg font-semibold mb-4">Tržby a zisk</h3>
			<ClientOnly fallback={<div className="h-[300px] animate-pulse bg-muted rounded" />}>
				<ResponsiveContainer width="100%" height={300}>
					<ComposedChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
						<CartesianGrid strokeDasharray="3 3" stroke="#E4E5F2" />
						<XAxis dataKey="year" fontSize={12} />
						<YAxis fontSize={12} tickFormatter={(v: number) => formatCurrency(v)} width={100} />
						<Tooltip
							formatter={(value, name) => [
								formatCurrency((value as number) ?? null),
								name === "revenue" ? "Tržby" : "Čistý zisk",
							]}
							labelFormatter={(label) => `Rok ${String(label)}`}
						/>
						<Legend formatter={(value) => (value === "revenue" ? "Tržby" : "Čistý zisk")} />
						<Bar dataKey="revenue" fill="#0072FF" radius={[4, 4, 0, 0]} />
						<Line
							type="monotone"
							dataKey="netProfit"
							stroke="#0E9F6E"
							strokeWidth={2}
							dot={{ fill: "#0E9F6E", r: 4 }}
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</ClientOnly>
		</div>
	);
}
